// The Facts surface (#11426).
//
// Facts are the one place the record is AUTHORED rather than read, and the old
// tab took that to mean the page should be a form: every attribute an input,
// every unheld field a placeholder, 289 rows deep. What it holds became
// unreadable behind what it could hold.
//
// So: it reads by default and edits in place. A row shows its value; hovering
// it reveals a pencil; clicking swaps that row into the existing editor, with
// all of its draft, autofill and review machinery intact.
//
// Closing the row is the save (#11880). The user closes it the way one leaves
// any input: Escape, Enter in a single-line field, tabbing on to the next
// control, or clicking anywhere else on the page. Each of those reports the
// close to the caller (`onClose`), which writes the drafts. Popovers the row
// owns — the entity picker's list, a hierarchical value picker — are portalled
// out of the row's box, so "anywhere else" is measured against the application
// root: a click that lands outside it is a click in a portal and is not a close.
//
// "One row at a time" is the intent but not the rule, and the difference is
// load-bearing: a row holding an unsaved draft or a save conflict stays open
// whatever else the user opens. Read mode renders the SERVER value and paints
// no conflict, so closing those rows hid a pending write, or hid a rejection the
// user has to act on. Openness is therefore derived, not merely stored — see
// `mustStayOpen`. The one draft that does NOT hold a row open is the one on
// the wire: a closed row reads the value it is saving (`savingView`) rather
// than keeping a blurred editor up for the round-trip, and reopens only if
// the save is rejected.
//
// At rest each card holds the FILLED facts, with the unfilled fields behind
// one in-place "Show N unfilled fields" disclosure per card (#11426, design
// round 10 — sixty consecutive dash rows was the card spending its height
// proving the gaps). An earlier pass rejected this fold because filling a
// blank field is half of what this tab is for; the round-10 shape answers
// that: the search matches folded fields (a searched-for field always
// surfaces), a row holding a draft or a conflict never folds, and the
// doorway's count keeps the gap visible. Held facts sort first inside each
// group, so the record's substance reads first and its gaps follow.
//
// That order is decided when the grid first shows the record and then holds
// for as long as the user is on it (#11896). Sorting on the live value moved
// a blank row to the top of its card the moment it was filled, away from
// where the user was typing, and the entry read as lost. Leaving the record
// and coming back is what re-sorts — the grid unmounts with the tab.
//
// Up to two column stacks rather than a row grid, so cards sit at their natural
// height and uneven groups never leave a mid-page gap. One stack per section up
// to two, filled sequentially, so a lone group spends the full width and the
// organization's configured group order survives a collapse to one column.

import React, { useEffect, useRef, useState } from "react";
import {
  hasFactValue,
  isDerivedFact,
  isLinkSet,
} from "../Overview/previewData";
import { Tooltip } from "@mui/material";
import { PencilSimpleIcon } from "@phosphor-icons/react";
import FactRenderer from "../Components/FactRenderer";
import { FactAutofillOffer } from "../Components/AutofillTriggers";
import { useAnswerStore } from "@/components/FpFundBrain/Matrix/AnswerDialog";
import PendingReviewBadge from "../Components/PendingReviewBadge";
import { Card, SectionHead } from "../../design/Card";
import { Denominator } from "../../design/Denominator";
import { Doorway } from "../../design/Doorway";
import { useEntityNavigate } from "../entityNavigation";
import {
  factDisplayName,
  isArrayFact,
  type ArrayFact,
  type ScalarFact,
} from "../types";
import { mappedAddressOf } from "../utils/address";
import AddressMap from "../Components/AddressMap";
import { useFrameWidth } from "@/hooks/useFrameWidth";

/** The Facts tab row's map under a mapped address (#13870): in the value
 *  column when the row is wide, under the whole row when it is narrow. */
const FACT_ROW_ADDRESS_MAP_HEIGHT = 200;
const FACT_ROW_UNDER_ROW_ADDRESS_MAP_HEIGHT = 160;
/** Below this row width the map leaves the value column for a line of its
 *  own under the row: the 148px key column plus the narrowest value column
 *  (360px) that still shows a readable map. */
const FACTS_MAP_UNDER_ROW_BELOW_PX = 148 + 360;

export type AnyFact = ScalarFact | ArrayFact;

/**
 * A fact whose value is computed from other rows, never stored (#14680): the
 * record detail serves the entity system columns (Date Created, Last
 * Interaction, ...) with derived provenance. Such a row reads its value and
 * offers no way to edit it, whatever the viewer may edit elsewhere.
 */
const isReadOnlyFact = (fact: AnyFact): boolean =>
  fact.provenance?.kind === "derived" || fact.provenance?.kind === "readonly";

export interface FactSection {
  key: string;
  label: string;
  facts: AnyFact[];
}

interface FactsGridProps {
  /** The record these facts belong to — the link-set doorway navigates to
   *  its Relationships tab, scoped to the fact it came from. */
  entity: { id: string; kind: string; name: string };
  sections: FactSection[];
  /** The existing editor, rendered for a row that is open. */
  renderEditor: (fact: AnyFact) => React.ReactNode;
  /** Rendered under a not-applicable row, naming what made it irrelevant. */
  notApplicableNote?: (fact: AnyFact) => string | null;
  /**
   * Rows that must stay open regardless of what the user last clicked —
   * anything holding an unsaved draft or a save conflict. Read mode shows the
   * SERVER value and paints no conflict, so closing such a row hides work the
   * user is about to commit, or a rejection they have to act on. The grid
   * stays presentational: the caller owns the draft store, not this component.
   */
  mustStayOpen: (fact: AnyFact) => boolean;
  /**
   * The user has left the rows they were editing (#11880): Escape, Enter in a
   * single-line input, focus moving on, or a click elsewhere on the page. The
   * grid drops its own record of those rows as this fires; the caller writes
   * the drafts, and rows holding one stay open through `mustStayOpen` until
   * it lands. Called once per close, after the inputs have committed whatever
   * the leaving focus made them commit, with the names of the rows that
   * closed: a row opened after the close began is still being edited, and
   * its draft is not part of this close (#13359).
   */
  onClose: (closed: ReadonlySet<string>) => void;
  /**
   * The user opened a row. A row closed behind a save in flight waits in the
   * caller's queue; opened again it is being edited, and its draft is not
   * that close's to send any more (#13359).
   */
  onOpen: (name: string) => void;
  /**
   * The fact as it will read once the save in flight lands, for a row whose
   * draft is on the wire; null otherwise. Such a row reads that value instead
   * of holding its editor open for the round-trip (#11880): an editor left
   * standing re-formats itself on blur and reads as a glitch. The draft is
   * still there behind it, so a rejected save reopens the row. Required for
   * the same reason `searchActive` is: the one caller always knows, and a
   * default would be a second path nothing exercises.
   */
  savingView: (fact: AnyFact) => AnyFact | null;
  canEdit: boolean;
  /**
   * A search term is narrowing the sections (#11426, design round 10):
   * every match shows regardless of filled state, so nothing the search
   * found hides behind the unfilled-fields disclosure. Required — the one
   * caller always knows, and a default would be a second path nothing
   * exercises.
   */
  searchActive: boolean;
}

// Delegates to the one definition of "held" — a local copy of the predicate
// is exactly how the legend once disagreed with the badge (#11426 A5).
const isPopulated = (fact: AnyFact): boolean => hasFactValue(fact as never);

// Values a link-set row lists before it defers to Relationships (#11426,
// keeper ruling, all-kinds sweep + register rule 20: no single row may bury
// its siblings). Ten matches the Relationships tab, because both are
// full-width tab surfaces — the rail's three is a rail cap, and one cap per
// surface scale is what lets a reader carry the grammar between them.
const LINK_ROW_CAP = 10;

/**
 * A link set on the Facts tab: capped, with its own denominator and a
 * doorway to the Relationships tab scoped to this fact.
 *
 * The tab's unit is the FACT, not the link. Rendering all 184 values inside
 * one row made the row a surface pretending to be a row, and took the tab's
 * completeness claim with it — the reader could not see that the record
 * holds three other facts, which is the one thing this tab exists to say.
 */
const LinkSetValue: React.FC<{
  fact: AnyFact;
  entity: { id: string; kind: string; name: string };
  /** Drawn beside the last listed value (see `FactRenderer`'s `trailing`). */
  trailing?: React.ReactNode;
}> = ({ fact, entity, trailing = null }) => {
  const entityNavigate = useEntityNavigate();
  const values = isArrayFact(fact) ? fact.array : [];
  const shown = values.slice(0, LINK_ROW_CAP);
  // A denominator states what you are NOT being shown. A row showing all of
  // its values is not withholding anything, so "1 of 1 linked records" is
  // noise — and a doorway to "the rest" would open on nothing.
  const withheld = values.length > shown.length;
  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {shown.map((item, index) => (
          <FactRenderer
            key={index}
            fact={{ ...fact, array: [item] } as AnyFact}
            trailing={index === shown.length - 1 ? trailing : null}
          />
        ))}
      </div>
      {withheld ? (
        <Denominator
          shown={shown.length}
          total={values.length}
          noun="linked records"
          trailing={
            // The row opens its editor on click, so the doorway has to keep
            // its own click: without this the navigation never happened and
            // the row went into edit mode instead.
            <span onClick={(e) => e.stopPropagation()}>
              <Doorway
                onClick={() =>
                  entityNavigate.navigateToTab(
                    entity as never,
                    "entities",
                    `?fact=${encodeURIComponent(factDisplayName(fact))}`
                  )
                }
              >
                Show all
              </Doorway>
            </span>
          }
        />
      ) : null}
    </>
  );
};

/**
 * The autofill run that wrote this value, or null for a value a person
 * entered. A single-value fact carries the run on the fact; a list fact
 * (select, multi-select, entity list) carries it on each item (all items of
 * one run share it), so the first item carrying one stands for the fact —
 * the editor's Source chip rule (#12035). A run is a query AND the
 * workspace it lives in; the answer cannot open without both.
 */
const derivedSource = (
  fact: AnyFact
): { workspaceId: string; queryId: string } | null => {
  const carrier = isArrayFact(fact)
    ? fact.array.find((v) => v.queryId && v.workspaceId)
    : fact;
  return carrier?.queryId && carrier.workspaceId
    ? { workspaceId: carrier.workspaceId, queryId: carrier.queryId }
    : null;
};

const FactRow: React.FC<{
  entity: { id: string; kind: string; name: string };
  /** The fact as stored. The editor always opens against this. */
  fact: AnyFact;
  /**
   * The fact as it will read once the save in flight lands, or null. Read
   * mode shows it in place of `fact`; the editor never sees it, because an
   * editor measuring its dirtiness against the value it is itself saving
   * reads as clean, drops the draft, and takes the pending view with it.
   */
  pending: AnyFact | null;
  editing: boolean;
  onEdit: () => void;
  /** Open because the user asked, rather than because a conflict forced it. */
  focusOnOpen: boolean;
  renderEditor: (fact: AnyFact) => React.ReactNode;
  note: string | null;
  canEdit: boolean;
}> = ({
  entity,
  fact,
  pending,
  editing,
  onEdit,
  focusOnOpen,
  renderEditor,
  note,
  canEdit,
}) => {
  const [hovered, setHovered] = useState(false);
  const shown = pending ?? fact;
  const source = derivedSource(shown);
  // A mapped address draws its map under the text (#13870). In a wide row
  // the map sits in the value column, where the editor draws its own; in a
  // narrow one the 148px key column would leave it a sliver, so it becomes a
  // second line of the row, the row's full width. The row is measured only
  // when it holds a map, and no map is drawn until that first measurement
  // lands: a narrow row never builds the in-column map (a second billed
  // Maps load) just to move it under the row a frame later.
  const mapped = mappedAddressOf(shown);
  const { frameRef, frameWidth } = useFrameWidth<HTMLDivElement>();
  const measured = frameWidth !== null;
  const narrow = measured && frameWidth < FACTS_MAP_UNDER_ROW_BELOW_PX;
  const mapInValue = mapped !== null && measured && !narrow;
  const mapUnderRow = narrow ? mapped : null;
  // The provenance mark is the doorway to the extraction, not a
  // decoration: a value you cannot check is a value you have to re-derive
  // by hand. It sits on the value's last line, whatever shape the value is.
  const mark = source ? (
    <Tooltip title="AI Reasoning" arrow>
      <button
        type="button"
        aria-label="View the AI answer that produced this value"
        onClick={() =>
          useAnswerStore
            .getState()
            .open(source.workspaceId, source.queryId, factDisplayName(fact))
        }
        style={{
          marginLeft: "var(--fp-gap-hair)",
          border: "none",
          background: "none",
          padding: 0,
          cursor: "pointer",
          color: "var(--fp-ink-4)",
          fontSize: "var(--fp-t-micro)",
        }}
      >
        ✦
      </button>
    </Tooltip>
  ) : null;
  const reviewBadge = (
    <PendingReviewBadge staging={!isArrayFact(shown) && !!shown.staging} />
  );
  const editRef = React.useRef<HTMLDivElement>(null);

  // Clicking the pencil unmounts the pencil, so focus fell to <body> — the
  // caret was nowhere and Escape, scoped to this grid, never saw the key. Put
  // the caret where the user just asked to type.
  //
  // Only for a row the USER opened: a row forced open by an arriving conflict
  // must not yank focus out of whatever they are currently editing.
  React.useEffect(() => {
    if (!editing || !focusOnOpen) return;
    editRef.current
      ?.querySelector<HTMLElement>("input, textarea, select, [tabindex]")
      ?.focus();
  }, [editing, focusOnOpen]);

  if (editing) {
    return (
      // The attribute is how the grid tells a click inside an open row from a
      // click elsewhere on the page, and which rows a close applies to (see
      // `FactsGrid`).
      <div
        ref={editRef}
        data-fact-row-open={fact.name}
        style={{ borderTop: "1px solid var(--fp-border)" }}
      >
        {renderEditor(fact)}
        {note ? (
          <div
            style={{
              fontSize: "var(--fp-t-fine)",
              color: "var(--fp-ink-3)",
              paddingBottom: "var(--fp-gap-tight)",
            }}
          >
            {note}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      ref={mapped ? frameRef : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: "var(--fp-gap-tight) 0",
        borderTop: "1px solid var(--fp-border)",
        fontFamily: "var(--fp-font-ui)",
        fontSize: "var(--fp-t-meta)",
        // A not-applicable row is muted in READ mode too. The dashed treatment
        // #10968 established used to come from the editor's wrapper, which the
        // grid renders only when a row is open — so the signal appeared only
        // after you opened the row, which is exactly backwards.
        ...(note ? { opacity: 0.65 } : null),
      }}
    >
      <div
        style={{
          display: "flex",
          gap: "var(--fp-gap)",
          alignItems: "baseline",
        }}
      >
        {/* The key is half the fact — the value is unusable without it. */}
        <span
          style={{
            width: "148px",
            flex: "none",
            color: "var(--fp-ink-3)",
            fontWeight: "var(--fp-w-primary)" as unknown as number,
          }}
        >
          {factDisplayName(fact)}
        </span>

        <span
          style={{
            flex: 1,
            minWidth: 0,
            color: "var(--fp-ink)",
            fontWeight: "var(--fp-w-primary)" as unknown as number,
          }}
        >
          {isPopulated(shown) ? (
            isLinkSet(shown as never) ? (
              <LinkSetValue fact={shown} entity={entity} trailing={mark} />
            ) : isArrayFact(shown) ? (
              // A list value is a stack of chips: the mark goes beside the
              // last chip, not on a line of its own under the stack.
              <FactRenderer fact={shown} trailing={mark} />
            ) : shown.entity ? (
              // A linked record is a one-line chip that lines up on its icon,
              // so a mark on the text baseline hung below the name: centre the
              // two on one row instead.
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  maxWidth: "100%",
                  minWidth: 0,
                }}
              >
                <FactRenderer fact={shown} />
                {mark}
              </span>
            ) : mapInValue ? (
              // The mark and the review clock ride on the address line, not
              // under the map.
              <FactRenderer
                fact={shown}
                trailing={
                  <>
                    {mark}
                    {reviewBadge}
                  </>
                }
                addressMap={{
                  address: mapped,
                  height: FACT_ROW_ADDRESS_MAP_HEIGHT,
                }}
              />
            ) : (
              <FactRenderer fact={shown} />
            )
          ) : canEdit && fact.ai_autofill ? (
            // An unfilled fact AI can fill is a different state from one it
            // cannot (#12035): the empty slot carries the offer, visible at
            // rest because it writes the record, and withheld from a viewer
            // who may not.
            <FactAutofillOffer
              publicEntityId={entity.id}
              attributeId={Number(fact.id)}
              fieldName={factDisplayName(fact)}
              lastOutcomeAt={fact.autofill_last_outcome_at ?? null}
            />
          ) : (
            <span style={{ color: "var(--fp-ink-4)" }}>—</span>
          )}
          {/* A single scalar value is inline text, so its mark follows it
            here; a list value and an address mapped in the value column took
            the same node as `trailing` above, and a linked record took it
            inside its row. */}
          {isPopulated(shown) &&
          !isArrayFact(shown) &&
          !shown.entity &&
          !mapInValue
            ? mark
            : null}
          {mapInValue ? null : reviewBadge}
          {note ? (
            <span
              style={{
                display: "block",
                fontSize: "var(--fp-t-fine)",
                color: "var(--fp-ink-3)",
              }}
            >
              {note}
            </span>
          ) : null}
        </span>

        {canEdit ? (
          <button
            type="button"
            aria-label={`Edit ${factDisplayName(fact)}`}
            onClick={onEdit}
            // Shown only on its row's hover (or focus): a still page's disabled drawing
            // leaves it hidden (#13370 round 42).
            data-hover-reveal=""
            style={{
              flex: "none",
              width: "22px",
              height: "22px",
              display: "grid",
              placeItems: "center",
              border: "none",
              background: "none",
              borderRadius: "var(--fp-gap-tight)",
              cursor: "pointer",
              color: "var(--fp-ink-4)",
              // Hover reveals it, but focus keeps it reachable without a mouse.
              opacity: hovered ? 1 : 0,
            }}
            onFocus={() => setHovered(true)}
            onBlur={() => setHovered(false)}
          >
            <PencilSimpleIcon size={12} />
          </button>
        ) : null}
      </div>
      {mapUnderRow ? (
        <AddressMap
          address={mapUnderRow}
          height={FACT_ROW_UNDER_ROW_ADDRESS_MAP_HEIGHT}
        />
      ) : null}
    </div>
  );
};

const FactsGrid: React.FC<FactsGridProps> = ({
  entity,
  sections,
  renderEditor,
  notApplicableNote,
  mustStayOpen,
  onClose,
  onOpen,
  savingView,
  canEdit,
  searchActive,
}) => {
  // Sections whose unfilled rows the user disclosed (#11426, round 10): at
  // rest the card holds the FILLED facts — sixty consecutive dash rows was
  // the card spending its height proving the gaps — and the unfilled fields
  // wait behind one in-place doorway per card.
  const [showUnfilled, setShowUnfilled] = useState<ReadonlySet<string>>(
    new Set()
  );
  // The rows the user opened. A SET, not a single name: deriving openness from
  // whether a row is dirty means a row closes the instant its text transiently
  // equals the server value — retype "2024" over "2024" and the last keystroke
  // collapses the row under the cursor and drops focus. What has to persist is
  // that the user opened it, which dirtiness is only a proxy for.
  const [opened, setOpened] = useState<ReadonlySet<string>>(new Set());

  const rootRef = useRef<HTMLDivElement>(null);
  // Read through a ref by the document listener below, so the listener is
  // attached once rather than re-bound on every render of the tab.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Leaving the open rows (#11880). Both the close and the save wait a frame,
  // and for the same reason: the inputs commit on the blur that follows a
  // mousedown (a list field turns its pending text into a value), and that
  // blur has not happened yet when the mousedown handler runs. Closing the
  // row synchronously unmounted its editor before the blur could fire, so
  // the value the user could still see in the box was the one value the
  // save left behind. Only the rows open NOW close, by name: a row the user
  // opens with the very click that closes these is not in the set and
  // survives the deferred update.
  const closeOpenRows = () => {
    const closing = new Set<string>();
    rootRef.current
      ?.querySelectorAll<HTMLElement>("[data-fact-row-open]")
      .forEach((row) => {
        const name = row.dataset.factRowOpen;
        if (name) closing.add(name);
      });
    // A frame, not a task: the browser's blur default action (triggered by
    // this same mousedown) completes synchronously before the event loop
    // reaches the next macrotask or animation frame, so this still waits
    // for it -- rAF is not a product timer (productTimers.ts), so it
    // needs no story-side clock (#12187).
    window.requestAnimationFrame(() => {
      setOpened((prev) => {
        const next = new Set(prev);
        for (const name of closing) next.delete(name);
        return next;
      });
      onCloseRef.current(closing);
    });
  };

  // Whether `target` is somewhere the user could only have reached by leaving
  // every open row: inside the application (not in a portal), and in none of
  // them. Portalled popovers are siblings of the application root under
  // <body>, so a click in one lands outside the root and is never "elsewhere".
  const isOutsideOpenRows = (target: EventTarget | null): boolean => {
    const root = rootRef.current;
    if (!root || !(target instanceof Node)) return false;
    let appRoot: HTMLElement = root;
    while (appRoot.parentElement && appRoot.parentElement !== document.body) {
      appRoot = appRoot.parentElement;
    }
    if (!appRoot.contains(target)) return false;
    const openRows = root.querySelectorAll("[data-fact-row-open]");
    if (openRows.length === 0) return false;
    for (const row of openRows) {
      if (row.contains(target)) return false;
    }
    return true;
  };

  useEffect(() => {
    // Capture phase, so a control that stops propagation on its own mousedown
    // (the hierarchical picker's portal does) still counts as a click for us —
    // and is then judged by where it is, which for a portal means ignored.
    const onMouseDown = (e: MouseEvent) => {
      if (isOutsideOpenRows(e.target)) closeOpenRows();
    };
    document.addEventListener("mousedown", onMouseDown, true);
    return () => document.removeEventListener("mousedown", onMouseDown, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Focus moving on to another control on the page — Tab out of the editor,
  // for one. A null target is a portal, an unmounting chip or the window
  // losing focus; none of those is the user leaving, and a click that follows
  // one is caught above.
  const onBlur = (e: React.FocusEvent) => {
    if (isOutsideOpenRows(e.relatedTarget)) closeOpenRows();
  };

  // Scoped to this grid, and skipped once something nearer the keypress has
  // already answered it. A `window` listener stole Escape from every other
  // consumer on the page: dismissing the entity picker, an answer dialog or the
  // settings modal also collapsed the row underneath, losing a typed search
  // term that is not a draft and so is not protected by `mustStayOpen`.
  //
  // Enter closes too, but only from a single-line input and only when nothing
  // nearer claimed it: the pickers and list fields use Enter to take a value
  // and prevent its default, and a textarea's Enter is a newline.
  //
  // Keys typed inside a dialog the row opened are the dialog's, not the
  // row's. The entity picker renders its create form in-tree, so a keydown
  // in there bubbles through the open row to here; closing the row on it
  // would save the record and unmount the half-filled form under the user.
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.defaultPrevented) return;
    if (
      e.target instanceof Element &&
      e.target.closest('[role="dialog"]') !== null
    ) {
      return;
    }
    if (e.key === "Escape") {
      closeOpenRows();
      return;
    }
    if (
      e.key === "Enter" &&
      !e.shiftKey &&
      e.target instanceof HTMLInputElement
    ) {
      closeOpenRows();
    }
  };

  // Held as the row READS, so a blank field being filled counts as held and
  // surfaces from the moment it closes rather than when the save lands — and
  // a field being cleared, the reverse.
  const held = (fact: AnyFact): boolean =>
    isPopulated(savingView(fact) ?? fact);

  // Whether a row was held when the grid first showed it, kept for the
  // grid's lifetime (#11896). This, not the live value, is what a row sorts
  // and stays on screen by: a fact filled or cleared while the user is on
  // the page keeps its place until they leave and come back. A row first
  // seen later (a search cleared, a conditional field revealed) is judged as
  // it arrives and then holds its place like the rest.
  const heldAtFirstSight = useRef(new Map<string, boolean>());
  const heldOnArrival = (fact: AnyFact): boolean => {
    const known = heldAtFirstSight.current.get(fact.name);
    if (known !== undefined) return known;
    const now = held(fact);
    heldAtFirstSight.current.set(fact.name, now);
    return now;
  };

  const ordered = sections.map((section) => {
    // The derived facts (#14680) are computed, never stored: they read after
    // the section's own attributes and count toward nothing, since Date
    // Created is held on every record and says nothing about how complete
    // this one is.
    const stored = section.facts.filter((f) => !isDerivedFact(f));
    const derived = section.facts.filter(isDerivedFact);
    return {
      ...section,
      // What the record held on arrival reads first; its gaps follow, in
      // schema order. The count is live: the footer and the card head say
      // what the record holds now.
      rows: [
        ...stored.filter(heldOnArrival),
        ...stored.filter((f) => !heldOnArrival(f)),
        ...derived.filter(heldOnArrival),
        ...derived.filter((f) => !heldOnArrival(f)),
      ],
      heldCount: stored.filter(held).length,
      storedCount: stored.length,
    };
  });

  const heldTotal = ordered.reduce((n, s) => n + s.heldCount, 0);
  const fieldTotal = ordered.reduce((n, s) => n + s.storedCount, 0);
  // The sections that hold any of the record's own attributes: a count tells
  // these apart, and a section holding none of them states no count.
  const countedSections = ordered.filter((s) => s.storedCount > 0).length;

  // One column per section up to two. A fixed pair of column elements meant a
  // single group rendered at half width beside dead space: `auto-fit` only
  // collapses tracks holding no items, and an empty column div is still an item.
  const columnCount = Math.min(2, ordered.length) || 1;

  // Filled sequentially, NOT alternately. `i % 2` scattered groups across the
  // two stacks, so the organization's configured order survived only within a
  // column, and a viewport narrow enough to collapse to one column painted
  // 0,2,4,1,3,5. Splitting into halves keeps document order correct at every
  // width, because the collapsed case is just the stacks concatenated.
  const perColumn = Math.ceil(ordered.length / columnCount);
  const columns: (typeof ordered)[] = Array.from(
    { length: columnCount },
    (_, i) => ordered.slice(i * perColumn, (i + 1) * perColumn)
  );
  // One section's card: a group, Ungrouped, or the one Facts card.
  const renderSection = (section: (typeof ordered)[number]) => (
    <div key={section.key} data-fact-group={section.key}>
      <Card>
        <SectionHead
          title={section.label}
          // A per-group count earns its place only when the groups
          // differ from each other. With one group the group IS the
          // record, so this would restate the record line a few
          // pixels above it, word for word (#11426). The count is of the
          // section's own attributes; an Ungrouped holding only derived
          // facts has none to count, and is not one of the groups that
          // tell each other apart (#14680).
          sub={
            countedSections > 1 && section.storedCount > 0
              ? `${section.heldCount} of ${section.storedCount}`
              : undefined
          }
          // No AI legend in the head (#11426 gaps-v12 item 5,
          // reversing round 8's quiet note): the ✦ mark on a row
          // carries its own meaning, and a legend repeated on every
          // card said it once per card.
        />
        {/* At rest the card holds the FILLED facts; the unfilled
            fields wait behind one in-place disclosure (#11426,
            round 10) — sixty dash rows was the card spending its
            height proving the gaps. A search, an unsaved draft or
            a conflict overrides the fold, so a row the user is
            looking for (or working in) never hides. The doorway
            counts the rows actually WITHHELD — a drafted-but-
            unfilled row is on screen and must not be counted as
            hidden. Like the other in-place disclosures (the
            rail's +N more), it is one-way: once opened, the card
            stays open until the record changes. A row on screen
            when the grid arrived stays on screen (#11896): a
            field the user clears keeps its place rather than
            folding away under them. */}
        {(() => {
          // Both held predicates, and neither is redundant: a
          // row that arrived unfilled and was filled through a
          // search has only its live value to keep it on screen
          // once the search clears.
          const visible = (fact: AnyFact) =>
            searchActive ||
            showUnfilled.has(section.key) ||
            heldOnArrival(fact) ||
            held(fact) ||
            mustStayOpen(fact);
          const withheld = section.rows.filter((fact) => !visible(fact)).length;
          return (
            <>
              {section.rows.filter(visible).map((fact) => {
                // A row with its draft on the wire reads the value
                // it is saving. The user may still reopen it by
                // hand; the editor then opens on the draft, so
                // what they see is what they edit.
                const pending = savingView(fact);
                const editable = canEdit && !isReadOnlyFact(fact);
                return (
                  <FactRow
                    key={fact.name}
                    entity={entity}
                    fact={fact}
                    pending={pending}
                    // Open is DERIVED, not merely stored. Modelling
                    // it as one piece of state said "one row at a
                    // time" while the draft store said otherwise,
                    // and the screen followed the weaker of the two.
                    editing={
                      opened.has(fact.name) ||
                      (pending === null && mustStayOpen(fact))
                    }
                    focusOnOpen={opened.has(fact.name)}
                    onEdit={() => {
                      setOpened((prev) => new Set(prev).add(fact.name));
                      onOpen(fact.name);
                    }}
                    renderEditor={renderEditor}
                    note={notApplicableNote?.(fact) ?? null}
                    canEdit={editable}
                  />
                );
              })}
              {withheld > 0 ? (
                <div style={{ paddingTop: "var(--fp-gap-tight)" }}>
                  <Doorway
                    onClick={() =>
                      setShowUnfilled((prev) => new Set(prev).add(section.key))
                    }
                  >
                    Show {withheld} unfilled{" "}
                    {withheld === 1 ? "field" : "fields"}
                  </Doorway>
                </div>
              ) : null}
            </>
          );
        })()}
      </Card>
    </div>
  );

  return (
    <div ref={rootRef} onKeyDown={onKeyDown} onBlur={onBlur}>
      <div
        data-fact-columns=""
        style={{
          display: "grid",
          // `auto-fit`, not a fixed `repeat(columnCount, …)`. The half-width
          // bug was never auto-fit's fault — it was the EMPTY second column
          // element, which is still a grid item and so held its track open.
          // Now that only non-empty stacks are rendered, auto-fit both fills
          // the width with one section and collapses to a single track when
          // two no longer fit, which is what keeps group order readable on a
          // narrow viewport. Pinning the count instead makes the cards overflow
          // sideways there rather than stack.
          gridTemplateColumns:
            "repeat(auto-fit, minmax(min(100%, 360px), 1fr))",
          gap: "var(--fp-page-gap)",
          alignItems: "start",
        }}
      >
        {columns.map((column, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--fp-page-gap)",
              minWidth: 0,
            }}
          >
            {column.map(renderSection)}
          </div>
        ))}
      </div>
      {/* The denominator goes where every other denominator goes: a footer
          sentence under the card, on the ground (round 8). It counts the
          record's own attributes; with none (a record type holding only
          derived facts, #14680) the tab's own empty state speaks instead,
          and a "0 of 0" would only restate it. */}
      {fieldTotal > 0 ? (
        <div
          style={{
            fontFamily: "var(--fp-font-ui)",
            fontSize: "var(--fp-t-meta)",
            color: "var(--fp-ink-4)",
            marginTop: "var(--fp-gap-tight)",
          }}
        >
          {heldTotal} of {fieldTotal} facts held
        </div>
      ) : null}
    </div>
  );
};

export default FactsGrid;
